import { z } from 'zod';
// Asset models — the two tables that feed the creative pipeline.
//
//   brand_assets    = founder-uploaded photography, illustrations,
//                     videos, documents. The raw material Mag picks
//                     from when she designs.
//   creative_assets = the rendered output of one concept × one format
//                     × one medium. Either Mag-generated (HTML/CSS via
//                     Playwright, fal.ai image) or curated (a direct
//                     pointer at a brand_asset).
//
// Both reference R2 URLs; both are brand-scoped. creative_assets
// optionally references a campaign / concept / source brand_asset so
// the regenerate path knows what to re-render.
// ─── brand_assets ────────────────────────────────────────────────────
export const brandAssetKindEnum = z.enum([
    /** Logo files. There can be multiple — light/dark variants, full
     *  wordmark vs mark-only, etc. */
    'logo',
    /** Product photography — the actual thing the founder sells. */
    'product_photo',
    /** Lifestyle / scene photography. */
    'lifestyle_photo',
    /** Illustrations, icons, abstract artwork. */
    'illustration',
    /** Video clips. Stills get extracted by sharp at upload time. */
    'video',
    /** Brand books, PDF style guides, anything documentary. Not
     *  rendered in ads, but readable by Mag for context. */
    'document',
]);
/** Vision auto-classification for an uploaded IMAGE asset (s216-54h.90).
 *  Replaces the error-prone per-batch manual "kind" dropdown: a small
 *  vision model looks at each file and assigns its kind + variant tags.
 *  `kind` is restricted to the visually-determinable categories — video
 *  + document are decided by MIME, not vision. `tags` surface the
 *  variant hints downstream pickers key on (e.g. the logo override
 *  looks for 'inverted' / 'white' to pick a light logo on dark
 *  surfaces; 'icon' / 'wordmark' distinguish lockup styles). */
export const brandAssetKindClassificationSchema = z.object({
    kind: z.enum(['logo', 'product_photo', 'lifestyle_photo', 'illustration']),
    tags: z.array(z.string().min(1).max(40)).max(6).default([]),
    reasoning: z.string().max(200).optional(),
});
/** Phase 4.29 — what's IN the image. Used by the fill agent to score
 *  "does this asset match the brief" (a brief asking for "founder at
 *  the bench" wants subject=people; an abstract motif wants
 *  subject=texture or style=illustration). 'logo-variant' marks
 *  scraped assets that turn out to be alternate logo crops (we
 *  surface them but don't double them with the canonical logo).
 *  'screenshot' + 'other' are rejection buckets — the classifier
 *  hides those so they stop polluting the picker. */
export const brandAssetSubjectEnum = z.enum([
    'people',
    'product',
    'environment',
    'texture',
    'illustration',
    'logo-variant',
    'screenshot',
    'other',
]);
/** Phase 4.29 — visual treatment. Distinct from subject: a 'people'
 *  subject can be 'photo' (real folks at a workshop) or 'illustration'
 *  (hand-drawn characters in a scene). Templates use this to ensure
 *  the brand's visual language carries through — e.g. BIKE's
 *  illustration-led brand prefers style=illustration assets even when
 *  the brief mentions people. */
export const brandAssetStyleEnum = z.enum(['photo', 'illustration', 'render', 'mixed']);
export const brandAssetSchema = z.object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    organizationId: z.string().uuid(),
    kind: brandAssetKindEnum,
    /** Public CDN URL. */
    url: z.string().url(),
    /** Thumbnail URL — 200×200 webp generated at upload time. Null for
     *  documents and videos (still extraction lands later). */
    thumbnailUrl: z.string().url().nullable(),
    /** Original filename — used for display in the gallery. */
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(120),
    bytes: z.number().int().nonnegative(),
    /** Free-text tags. Mag reads these when she picks assets — "hero",
     *  "product-x", "lifestyle-2025-summer". */
    tags: z.array(z.string().min(1).max(60)).default([]),
    /** Who uploaded this. Null for system-seeded assets (e.g. logo
     *  pulled by the scraper). */
    uploadedByUserId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    /** Phase 4.29 — classifier-derived metadata. Populated by the
     *  vision classifier (brand-image-classify agent) after the scraper
     *  ingests a row. Null on manually uploaded rows + scraped rows
     *  the classifier hasn't reached yet. Used by the fill agent to
     *  pick brand-native imagery matching a brief's subject/mood. */
    subject: brandAssetSubjectEnum.nullable().optional(),
    style: brandAssetStyleEnum.nullable().optional(),
    mood: z.string().max(120).nullable().optional(),
    /** Up to ~5 hex strings the classifier reads off the image. */
    dominantColors: z.array(z.string().min(1).max(32)).nullable().optional(),
    /** Phase 4.29 — soft delete. Set when classifier rejects (screenshot,
     *  competitor UI, generic noise). The asset stays in the table for
     *  audit + re-classification; gallery + picker queries filter it. */
    hiddenAt: z.string().datetime().nullable().optional(),
    /** Phase 4.28 — provenance of scraped assets. Null for manual
     *  uploads. */
    sourceUrl: z.string().url().nullable().optional(),
    scrapedAt: z.string().datetime().nullable().optional(),
});
// ─── creative_assets ─────────────────────────────────────────────────
export const creativeAssetGeneratorEnum = z.enum([
    /** HTML/CSS template rendered to PNG via Playwright. The default
     *  path for typography-led ads. */
    'html_css',
    /** fal.ai image generation. Used for product photography,
     *  illustration, photoreal scenes. */
    'fal_image',
    /** A direct re-use of an existing brand_asset (no generation). The
     *  source_asset_id field points at the row. */
    'curated',
    /** A creative asset that was uploaded directly for one campaign
     *  (e.g. founder dropped in a custom Photoshop export). */
    'direct_upload',
]);
export const creativeAssetPlatformEnum = z.enum(['meta', 'instagram']);
export const creativeAssetFormatEnum = z.enum([
    /** 1:1 feed (1080×1080) — Facebook + Instagram feed. */
    'feed_1x1',
    /** 4:5 portrait feed (1080×1350) — Instagram feed preferred ratio. */
    'feed_4x5',
    /** 9:16 full-bleed (1080×1920) — Stories + Reels. */
    'story_9x16',
    /** 1:1 carousel card — same dims as feed but used inside a multi-card unit. */
    'carousel_1x1',
    /** Organic post — same dims as feed_1x1 but explicitly tagged for the
     *  organic publish path. Keeps the paid/organic intent clear at render. */
    'profile_post',
    /** s220 — 9:16 animated story (1080×1920 MP4). Same dimensions as
     *  story_9x16 but the _video suffix routes the asset through the
     *  animated render path (CSS-keyframed React template → frame-step
     *  screenshots → FFmpeg → H.264 MP4 → R2). Use this format when the
     *  drop concept's format is 'video' and you want motion-graphics
     *  output rather than a static image. */
    'story_9x16_video',
    /** s224 — 1:1 animated feed video (1080×1080 MP4). Same render
     *  path as story_9x16_video; the template adapts its layout to
     *  the requested dimensions via dimensionsForFormat. Use when
     *  the placement is the Meta/IG main feed and a square format
     *  fits the audience-stage better than full-vertical. */
    'feed_1x1_video',
    /** s224 — 4:5 portrait animated feed video (1080×1350 MP4).
     *  Instagram's preferred feed video aspect — gives more vertical
     *  real estate without going full-bleed story. Best when the
     *  hook benefits from larger type but the placement is feed
     *  (not story/reel). */
    'feed_4x5_video',
    /** s226 — 1:1 animated carousel slide (1080×1080 MP4). Same
     *  dimensions as carousel_1x1 but the _video suffix routes it
     *  through Remotion. Used in MIXED-MEDIA carousels where some
     *  slides are static images + others are looping video — e.g.,
     *  slide 1 is a 4-second hook video, slides 2-4 are static
     *  product shots. All slides share carousel_group_id; per-slide
     *  format lets each row render natively. */
    'carousel_1x1_video',
]);
export const creativeAssetStatusEnum = z.enum([
    /** Row exists, fill data is set, but the worker hasn't rendered yet. */
    'pending',
    /** Worker is currently rendering. */
    'rendering',
    /** Render landed, URL is live. */
    'ready',
    /** Render failed — error is in `error_message`. */
    'failed',
]);
export const creativeAssetSchema = z.object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    organizationId: z.string().uuid(),
    /** Optional — set when the creative belongs to a specific campaign. */
    campaignId: z.string().uuid().nullable(),
    /** Optional — concept the creative was generated from. Concept rows
     *  arrive in a later session; the column is here so the schema
     *  doesn't need to change. */
    conceptId: z.string().uuid().nullable(),
    generator: creativeAssetGeneratorEnum,
    /** For html_css generator: which template was used. Key into the
     *  template registry in packages/creative. */
    templateKey: z.string().min(1).max(120).nullable(),
    /** For html_css: the JSON Mag wrote to fill the template (the
     *  template's Zod schema validates this). Stored as jsonb so the
     *  re-render path can replay exactly. */
    templateFill: z.unknown().nullable(),
    /** For fal_image: the prompt sent to the model. */
    prompt: z.string().min(1).max(4000).nullable(),
    /** For curated: the brand_assets row we point at. */
    sourceAssetId: z.string().uuid().nullable(),
    /** Public CDN URL — null until status === 'ready'. */
    url: z.string().url().nullable(),
    thumbnailUrl: z.string().url().nullable(),
    /** Which Meta surface this is sized for. Null when the creative is
     *  format-agnostic (rare; mainly the source render before it gets
     *  resized into per-format variants). */
    platform: creativeAssetPlatformEnum.nullable(),
    format: creativeAssetFormatEnum,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    status: creativeAssetStatusEnum,
    /** Set when status === 'failed'. */
    errorMessage: z.string().max(2000).nullable(),
    /** Ordered iteration log — appended each time the founder requests
     *  changes. Surfaced in the drawer so the founder sees the whole
     *  conversation that produced the current draft. */
    iterations: z
        .array(z.object({
        steering: z.string().min(1).max(800),
        at: z.string().datetime(),
    }))
        .default([]),
    /** Carousel grouping (M1 s159.6). When this row is one slide of a
     *  carousel, every slide shares the same carouselGroupId; the
     *  dashboard groups by it so a 5-slide carousel renders as ONE
     *  gallery tile, not five. Null on single-image creative. */
    carouselGroupId: z.string().uuid().nullable(),
    /** Zero-based slide position. Null on single-image creative. */
    slideIndex: z.number().int().nonnegative().nullable(),
    /** s222/s228 — render duration override for video formats. Mag
     *  picks this at draft time (s222 single, s226 per-carousel-slide);
     *  s228 reading-aware floor may bump it up; s233.3 lets the
     *  founder override post-render. Null for static formats; null on
     *  video formats means the render worker uses the template meta
     *  default. Range 4-12. */
    videoDurationOverrideSeconds: z.number().int().min(4).max(12).nullable(),
    createdAt: z.string().datetime(),
    /** Set when status moves into a terminal state (ready or failed). */
    renderedAt: z.string().datetime().nullable(),
});
//# sourceMappingURL=asset.js.map